import React, { useEffect, useContext, useMemo } from 'react';
import first from 'lodash.first';

import { PromiseState } from 'react-refetch';

import getDownloadKPIURL from 'url-constructors/getDownloadKPIURL';

import salesTypes from 'maps/sales-types';

import connect from 'helpers/apiConnect';
import { EntityContext } from 'context/entityContext';

import OverviewContainer from 'components/Overview/Container/Container';

import useUrlForTopic from 'components/data-containers/Overview/shared/utils/useUrlForTopic';
import metrics from 'maps/metrics';
import DownloadKPIs from './DownloadKPIs';
import { TimeFrames } from 'components/TopicFilters/shared';
import { IDownloadKPIData } from './types';
import { EntityTypes } from 'context/entityContext';

function dataSources(): any {
  return {
    fetchKPIData: (args: {
      entityIds: string[];
      entityType: EntityTypes;
      countryFilter: string;
      region: string;
      compareMode?: string;
      customRegion?: string | string[];
    }): { kpiData: string } => ({
      kpiData: getDownloadKPIURL(args),
    }),
  };
}

function getLocalSortBy(globalSortBy: TimeFrames): string {
  switch (globalSortBy) {
    case 'lw':
      return 'performance_last_week';
    case 'twtd':
    default:
      return 'count_this_week_to_date';
  }
}

interface IDownloadPerformanceProps {
  fetchKPIData: Function;
  kpiData: PromiseState<IDownloadKPIData[]>;
}

/*
 * The download performance card shows KPIs based on the topic dropdown
 * and a “Release to date” KPI.
 *
 * ## Differences per entity type
 *
 * ### Artist
 * Two column grid with KPIs for tracks and releases
 *
 * ### Track, release, project
 * KPIs for the current entity type
 *
 * Note: as the `sortBy` parameter differs per endpoint, the global sort metric
 * is converted to one supported by this data container.
 */
const DownloadPerformance: React.FC<IDownloadPerformanceProps> = (props) => {
  const { fetchKPIData, kpiData = PromiseState.create() } = props;

  const ctx = useContext(EntityContext);
  const {
    entityIds,
    entityType,
    viewSettings,
    filters,
    compareMode,
    customRegionFilter,
  } = ctx;
  const timeframe = viewSettings?.timeframe ?? 'twtd';

  useEffect(() => {
    fetchKPIData({
      entityIds,
      entityType,
      countryIds: filters?.country ?? undefined,
      region: filters?.region,
      compareMode,
      customRegion: customRegionFilter,
    });
  }, [
    entityIds,
    entityType,
    fetchKPIData,
    filters,
    compareMode,
    customRegionFilter,
  ]);

  const kpis = useMemo(() => {
    if (kpiData.fulfilled) {
      const data = kpiData.value;
      return first(data);
    }
  }, [kpiData]);

  const sortByValue = getLocalSortBy(timeframe);
  const sortByMetric = metrics[sortByValue];

  return (
    <OverviewContainer
      filtersToShow={['country', 'region', 'customRegion']}
      title="Download Performance"
      subtitle={salesTypes.downloads.label}
      linkTo={useUrlForTopic('download')}
    >
      <DownloadKPIs
        sortByMetric={sortByMetric}
        kpis={kpis}
        entityType={entityType}
        isPending={kpiData.pending}
      />
    </OverviewContainer>
  );
};

export default connect(dataSources as any)(DownloadPerformance as any);
